import { ContentfulSmCourse } from 'src/graphql/queries/GetSmCourse';
import { LearningProgressStatus, ProgressForCourses, ProgressForLessons } from 'src/services/http/progress';
import { getSlug } from 'src/utils/slug';
import { ContentfulLearningPathWithNavigation } from 'src/graphql/queries/GetLearningPathWithNavigation';

const findNextCourse = (
  currentLearningPath: ContentfulLearningPathWithNavigation,
  currentCourse: ContentfulSmCourse,
  completedCourses: string[],
): ContentfulSmCourse | null => {
  const currentCourseIndex = currentLearningPath.coursesCollection.items.findIndex(
    (course) => course.sys.id === currentCourse.sys.id,
  );

  const nextCourseBeforeCurrent = currentLearningPath.coursesCollection.items.find(
    (c, index) => index < currentCourseIndex && !completedCourses.includes(c.sys.id),
  ) as ContentfulSmCourse;

  const nextCourseAfterCurrent = currentLearningPath.coursesCollection.items.find(
    (c, index) => index > currentCourseIndex && !completedCourses.includes(c.sys.id),
  ) as ContentfulSmCourse;

  return nextCourseBeforeCurrent || nextCourseAfterCurrent || null;
};

export const findNextLessonSlug = (
  nextCourse: ContentfulSmCourse | null,
  lessonProgress: ProgressForLessons | undefined,
): string | null => {
  if (!nextCourse) {
    return null;
  }

  const nextCourseLessons = nextCourse.lessonsCollection.items;

  const inProgressLessonIds = (lessonProgress?.lessons || [])
    .filter((lesson) => lesson.status === LearningProgressStatus.IN_PROGRESS)
    .map((lesson) => lesson.lessonId);

  const completedLessonIds = (lessonProgress?.lessons || [])
    .filter((lesson) => lesson.status === LearningProgressStatus.COMPLETED)
    .map((lesson) => lesson.lessonId);

  const firstLessonSlug = getSlug(nextCourseLessons[0]);

  if (inProgressLessonIds.length === 0 && completedLessonIds.length === 0) {
    return firstLessonSlug;
  }

  const firstInProgressLesson = nextCourseLessons.find((lesson) => inProgressLessonIds.includes(lesson.sys.id));

  if (!firstInProgressLesson) {
    const lastCompletedLessonId = completedLessonIds[completedLessonIds.length - 1];
    const nextLessonIndex = nextCourseLessons.findIndex((lesson) => lesson.sys.id === lastCompletedLessonId) + 1;

    return getSlug(nextCourseLessons[nextLessonIndex]) || firstLessonSlug;
  }

  return getSlug(firstInProgressLesson) || firstLessonSlug;
};

export const getNextCourse = (
  currentLearningPath: ContentfulLearningPathWithNavigation | undefined,
  currentCourse: ContentfulSmCourse | undefined,
  courseProgress: ProgressForCourses | undefined,
): ContentfulSmCourse | null => {
  if (!courseProgress || !currentCourse || !currentLearningPath) {
    return null;
  }

  const nextCourse = findNextCourse(
    currentLearningPath,
    currentCourse,
    courseProgress.courses.filter((p) => p.status === LearningProgressStatus.COMPLETED).map((p) => p.courseId),
  );

  if (!nextCourse) {
    return null;
  }

  return nextCourse;
};
